
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { getPublicKitchenOrders } from '../../../../../services/Admin/OrderProcessingScreensApi';

// دالة لتنسيق الوقت بنظام 12 ساعة مع ص/م
function formatTime12(timeValue) {
  if (!timeValue) return '-';
  const [h, m] = String(timeValue).split(':');
  const hour24 = Number(h);
  const minute = Number(m);
  if (Number.isNaN(hour24) || Number.isNaN(minute)) return timeValue;
  const period = hour24 >= 12 ? 'م' : 'ص';
  const hour12 = ((hour24 + 11) % 12) + 1;
  const minuteText = String(minute).padStart(2, '0');
  return `${hour12}:${minuteText} ${period}`;
}

const normalizeOrdersResponse = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload;
  }

  if (typeof payload === 'object') {
    return Object.values(payload).reduce((accumulator, value) => {
      if (Array.isArray(value)) {
        return [...accumulator, ...value];
      }
      return accumulator;
    }, []);
  }

  return [];
};

const OrdersScreen = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchOrdersData = useCallback(async (isInitial = false) => {
    if (isInitial) setInitialLoading(true);
    if (isInitial) setLoading(true); // فقط أول تحميل
    setError('');
    try {
      const response = await getPublicKitchenOrders();
      let ordersList = normalizeOrdersResponse(response?.data);
      // ترتيب الطلبات الأحدث أولاً حسب created_at أو id
      ordersList = ordersList.slice().sort((a, b) => {
        // إذا كان يوجد created_at استخدمه، وإلا استخدم id
        const aTime = a.created_at ? new Date(a.created_at).getTime() : (a.id || 0);
        const bTime = b.created_at ? new Date(b.created_at).getTime() : (b.id || 0);
        return bTime - aTime;
      });
      setOrders(ordersList);
    } catch (err) {
      console.error('خطأ في جلب بيانات شاشة الأوردرات:', err);
      setError(err.message || 'حدث خطأ في جلب البيانات');
    } finally {
      if (isInitial) setLoading(false); // فقط أول تحميل
      if (isInitial) setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrdersData(true); // أول تحميل فقط
    const interval = setInterval(() => {
      fetchOrdersData(); // تحديث تلقائي بدون إخفاء الطلبات
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchOrdersData]);

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const getOrderStatusChip = (status) => {
    const normalizedStatus = status || 'pending';
    const statusMap = {
      pending: { label: 'قيد التجهيز', color: 'error' },
      in_preparation: { label: 'في التحضير', color: 'warning' },
      in_prepration: { label: 'في التحضير', color: 'warning' },
      out_of_preparation: { label: 'خارج التحضير', color: 'secondary' },
      pickup: { label: 'تم الاستلام من العميل', color: 'success' },
      finished: { label: 'مكتمل', color: 'success' },
      canceled: { label: 'ملغي', color: 'error' },
      cancelled: { label: 'ملغي', color: 'error' },
    };
    const config = statusMap[normalizedStatus] || {
      label: normalizedStatus,
      color: 'default',
    };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getItemStatusChip = (status) => {
    const normalizedStatus = status || 'pending';
    const statusMap = {
      pending: { label: 'قيد التجهيز', color: 'error' },
      in_preparation: { label: 'في التحضير', color: 'warning' },
      in_prepration: { label: 'في التحضير', color: 'warning' },
      out_of_preparation: { label: 'خارج التحضير', color: 'secondary' },
      pickup: { label: 'تم الاستلام من العميل', color: 'success' },
      finished: { label: 'تم التحضير', color: 'success' },
      canceled: { label: 'ملغي', color: 'error' },
      cancelled: { label: 'ملغي', color: 'error' },
    };
    const config = statusMap[normalizedStatus] || {
      label: normalizedStatus,
      color: 'default',
    };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return '-';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (loading) {
    // عند التحديث التلقائي لا تظهر أي علامة تحميل في منتصف الصفحة
    return null;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box dir="rtl">


      {orders.length > 0 ? (
        <Grid container spacing={1.2}>
          {orders.map((order) => {
            // تحقق إذا كانت كل الأصناف جاهزة للاستلام
            const allReadyForPickup = Array.isArray(order.order_items) && order.order_items.length > 0 && order.order_items.every(item => item.status === 'pickup');
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={order.id}>
                <Paper
                  sx={{
                    p: 1.2,
                    minHeight: 180,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: allReadyForPickup ? '2px solid #43a047' : undefined,
                    background: allReadyForPickup ? 'linear-gradient(90deg, #e8f5e9 0%, #f1f8e9 100%)' : undefined,
                  }}
                >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    طلب رقم #{order.general_increment}
                    {order.order_type === 'tables' && (
                      <span style={{ fontSize: '1rem', color: '#388e3c', marginRight: 12 }}>
                        {order.table?.name ? `- طاولة ${order.table.name}` : order.table?.number ? `- طاولة #${order.table.number}` : order.table_id ? `- طاولة #${order.table_id}` : ''}
                      </span>
                    )}
                  </Typography>
                  {getOrderStatusChip(order.status)}
                </Box>

                <Grid container spacing={0.5} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">نوع الطلب</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{order.order_type || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">العميل</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{order.customer?.name || '-'}</Typography>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">وقت الفتح</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatTime12(order.open_time)}</Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  عناصر الطلب
                </Typography>

                {Array.isArray(order.order_items) && order.order_items.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', borderRadius: 1 }}>
                    <Table size="small" dir="rtl">
                      <TableHead>
                        <TableRow>
                          <TableCell align="right" sx={{ fontWeight: 'bold', py: 0.5, px: 0.5 }}>العنصر</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', py: 0.5, px: 0.5 }}>الكمية</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', py: 0.5, px: 0.5 }}>الحالة</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {order.order_items.map((item) => {
                          const itemName = item.product?.name || item.drinks?.name || '-';
                          return (
                            <TableRow key={item.id}>
                              <TableCell align="right" sx={{ py: 0.5, px: 0.5 }}>{itemName}</TableCell>
                              <TableCell align="right" sx={{ py: 0.5, px: 0.5 }}>{item.quantity || '-'}</TableCell>
                              <TableCell align="right" sx={{ py: 0.5, px: 0.5 }}>{getItemStatusChip(item.status)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">لا توجد عناصر داخل هذا الطلب</Typography>
                )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LocalShippingIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد بيانات لعرضها
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default OrdersScreen;
